import { useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { VolumeX, Volume2 } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import Magnetic from "./Magnetic";

const POSTER = "/assets/hero-poster.webp";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 1.1 + i * 0.16, duration: 1.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HeroVideo = () => {
  const [videoFailed, setVideoFailed] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const vScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const vBright = useTransform(scrollYProgress, [0, 1], ["brightness(1)", "brightness(0.4)"]);
  const cOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const cY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) v.play().catch(() => {});
  };

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[580px] overflow-hidden bg-[#1C1417]">
      <motion.div style={{ scale: vScale, filter: vBright }} className="absolute inset-0 will-change-transform">
        {!videoFailed ? (
          <video
            ref={videoRef}
            data-testid="hero-video"
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={POSTER}
          >
            <source src="/assets/bacio-hero.mp4" type="video/mp4" />
            <source src="/assets/bacio-hero.webm" type="video/webm" onError={() => setVideoFailed(true)} />
          </video>
        ) : (
          <img
            data-testid="hero-fallback-image"
            src={POSTER}
            alt="Skinfill Bacio — beautifully hydrated natural lips"
            className="absolute inset-0 w-full h-full object-cover animate-ken-burns"
          />
        )}
      </motion.div>

      <div className="absolute inset-0 bg-gradient-to-b from-[#1C1417]/45 via-transparent to-[#1C1417]/70 pointer-events-none" />
      <div className="absolute inset-0 bg-[#A31D4F]/[0.07] mix-blend-overlay pointer-events-none" />

      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 1.4, ease: "easeOut" }}
        className="absolute inset-0 bg-[#1C1417] z-20 pointer-events-none"
      />

      <motion.div
        style={{ opacity: cOpacity, y: cY }}
        className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-24 sm:pb-24"
      >
        <div className="overflow-hidden mb-4">
          <motion.p
            custom={0}
            variants={line}
            initial="hidden"
            animate="show"
            className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.45em] text-[#F4EBE6]/90"
          >
            SKINFILL BACIO®
          </motion.p>
        </div>
        <h1
          data-testid="hero-main-heading"
          className="font-serif font-light text-[#FAF7F5] leading-[0.96] tracking-tight text-[14vw] sm:text-8xl lg:text-[7.5rem]"
        >
          <span className="block overflow-hidden">
            <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block">
              THE LIP
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span custom={2} variants={line} initial="hidden" animate="show" className="block italic text-[#E893AE]">
              BOOSTER
            </motion.span>
          </span>
        </h1>
        <div className="overflow-hidden mt-6">
          <motion.p
            custom={3}
            variants={line}
            initial="hidden"
            animate="show"
            className="font-serif italic font-light text-[#F4EBE6]/90 text-lg sm:text-2xl tracking-wide"
          >
            Don't change your lips. Boost them.
          </motion.p>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2, duration: 0.9 }}
          className="mt-10"
        >
          <Magnetic>
            <button
              data-testid="hero-cta-discover"
              onClick={() => scrollToId("#product")}
              className="btn-lux btn-lux-light"
            >
              <span>Discover Bacio</span>
            </button>
          </Magnetic>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 1 }}
        style={{ opacity: cOpacity }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        data-testid="hero-scroll-indicator"
      >
        <div className="w-[22px] h-9 rounded-full border border-[#FAF7F5]/40 flex justify-center pt-1.5">
          <div className="w-1 h-1.5 rounded-full bg-[#FAF7F5]/80 animate-scroll-dot" />
        </div>
      </motion.div>

      {!videoFailed && (
        <motion.button
          data-testid="hero-sound-toggle"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2.2, duration: 0.7 }}
          style={{ opacity: cOpacity }}
          onClick={toggleSound}
          aria-label={muted ? "Turn sound on" : "Turn sound off"}
          aria-pressed={!muted}
          className="absolute bottom-6 right-5 sm:right-8 z-10 w-11 h-11 rounded-full bg-[#1C1417]/40 backdrop-blur-md border border-[#FAF7F5]/30 flex items-center justify-center text-[#FAF7F5] hover:bg-[#A31D4F]/60 hover:border-[#E893AE]/50 transition-colors duration-300"
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </motion.button>
      )}
    </section>
  );
};

export default HeroVideo;
